<script lang="ts">
    import { onDestroy, onMount } from 'svelte';

    import { themeType as ThemeTypeEnum } from '../constants/Enums';
    import { JUMP_TYPE_ENUM } from '../enums';
    import { AWARD_PAGE_CK } from '../enums/track';
    import {
        awardCountdownSecondsLeft,
        cookieConsentBottomInsetPx,
        loadingWeb2app,
        loadingH5
    } from '../store/index';
    import { JumpLink } from '../utils/jumpTo';
    import { trackEvent } from '../utils/track';

    const TOTAL_SECONDS = 60;
    let secondsLeft = TOTAL_SECONDS;
    let timerId: number | undefined;
    let rootEl: HTMLDivElement | null = null;
    let intersectionObserver: IntersectionObserver | undefined;
    let isInViewport = true;

    const web2appJumped = false;
    const h5Jumped = false;

    export let themeType: ThemeTypeEnum = ThemeTypeEnum.WHITE;

    const stopTimer = () => {
        if (timerId) window.clearInterval(timerId);
        timerId = undefined;
    };

    const startTimer = () => {
        if (timerId) return;

        timerId = window.setInterval(() => {
            secondsLeft -= 1;
            awardCountdownSecondsLeft.set(secondsLeft);

            if (secondsLeft <= 0) {
                stopTimer();
                handleCountdownAutoJump();
            }
        }, 1000);
    };

    const resetAndStart = () => {
        stopTimer();
        secondsLeft = TOTAL_SECONDS;
        awardCountdownSecondsLeft.set(secondsLeft);
        startTimer();
    };

    const runJump = async (jumpType: string, button_name: string) => {
        if (jumpType === JUMP_TYPE_ENUM.TO_WEB2APP) {
            if (web2appJumped) return;
        } else if (jumpType === JUMP_TYPE_ENUM.TO_H5CREDITCARD) {
            if (h5Jumped) return;
        }

        try {
            await JumpLink(jumpType, { awardCountdownNumber: secondsLeft });
        } finally {
            await new Promise(resolve => setTimeout(resolve, 1000));
            $loadingWeb2app = false;
            $loadingH5 = false;
        }
    };

    const handleNextClick = async () => {
        trackEvent(AWARD_PAGE_CK, {
            button_name: 'cta'
        });
        $loadingH5 = true;
        await runJump(JUMP_TYPE_ENUM.TO_H5CREDITCARD, JUMP_TYPE_ENUM.TO_H5CREDITCARD);
    };

    const handleCountdownAutoJump = async () => {
        await runJump(JUMP_TYPE_ENUM.TO_H5CREDITCARD, JUMP_TYPE_ENUM.TO_H5CREDITCARD);
    };

    onMount(() => {
        const onVisibilityOrRestore = () => {
            const visible = document.visibilityState === 'visible';

            if (!visible) {
                stopTimer();

                return;
            }

            if (isInViewport) resetAndStart();
        };

        document.addEventListener('visibilitychange', onVisibilityOrRestore);
        window.addEventListener('pageshow', onVisibilityOrRestore);

        if ('IntersectionObserver' in window) {
            intersectionObserver = new IntersectionObserver(
                entries => {
                    const entry = entries[0];

                    isInViewport = Boolean(entry?.isIntersecting);

                    if (!isInViewport) {
                        stopTimer();

                        return;
                    }

                    if (document.visibilityState === 'visible') resetAndStart();
                },
                { threshold: 0.01 }
            );

            if (rootEl) intersectionObserver.observe(rootEl);
        }

        if (document.visibilityState === 'visible') resetAndStart();

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityOrRestore);
            window.removeEventListener('pageshow', onVisibilityOrRestore);
        };
    });

    onDestroy(() => {
        stopTimer();
        awardCountdownSecondsLeft.set(null);
        if (intersectionObserver) intersectionObserver.disconnect();
    });
</script>

<div
    bind:this={rootEl}
    class="download-countdown"
    class:download-countdown--black={themeType === ThemeTypeEnum.BLACK}
    class:download-countdown--white={themeType === ThemeTypeEnum.WHITE}
>
    <div
        class="countdown-line"
        class:countdown-line--black={themeType === ThemeTypeEnum.BLACK}
    >
        <div
            class="countdown-label"
            class:countdown-label--black={themeType === ThemeTypeEnum.BLACK}
            class:countdown-label--white={themeType === ThemeTypeEnum.WHITE}
            >Estás a punto de iniciar tu solicitud：
        </div>
        <div
            class="countdown-number"
            class:countdown-number--black={themeType === ThemeTypeEnum.BLACK}
            class:countdown-number--white={themeType === ThemeTypeEnum.WHITE}
            > {secondsLeft}s</div
        >
    </div>

    <div
        class="download-btn"
        class:download-btn--loading={$loadingWeb2app}
        role={null}
        aria-disabled={h5Jumped}
        on:click={() => handleNextClick()}
    >
        {#if $loadingWeb2app}
            <div class="loading-dots loading-dots--light">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
            </div>
        {:else}
          Continuar con la solicitud
        {/if}
    </div>
</div>

<style lang="scss">
    .download-countdown {
        position: fixed;
        margin: 0 auto;
        left: 0;
        right: 0;
        bottom: 0;
        width: 7.5rem;
        z-index: 20;
        padding: 0.18rem 0.4rem 0.52rem 0.4rem;
        box-sizing: border-box;
        pointer-events: none;
    }
    .download-countdown--black {
        background-color: #000104;
    }
    .download-countdown--white {
        background-color: #ffffff;
    }

    .countdown-line {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top:0.18rem;
        font-family: DiDiSans-Pro-Regular;
        margin-bottom: 0.18rem;
        pointer-events: none;
        white-space: nowrap;
        .countdown-label {
            font-size: 0.24rem;
            line-height: 0.34rem;
            font-family: DiDiSans-Pro-Regular;
        }
        .countdown-label--black {
            color: #ffffff;
        }
        .countdown-label--white {
            color: #0c0c0d;
        }
    }
    .countdown-line--black {
        margin-bottom: 40px;
    }

    .countdown-number {
        font-size: 0.32rem;
        color: #ff5f14;
        line-height: 0.4rem;
        font-family: DiDiSans-Pro-Bold;
    }

    .download-btn {
        pointer-events: auto;
        width: 100%;
        padding: 28px 32px;
        background: #FF5F14;
        height: 1.04rem;
        border-radius: 0.2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: DiDiSans-Pro-Bold;
        font-size: 0.4rem;
        line-height: 0.48rem;
        color: #ffffff;
        user-select: none;

        &.download-btn--loading {
            pointer-events: none;
        }
    }

    .loading-dots {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.1rem;

        .dot {
            width: 0.16rem;
            height: 0.16rem;
            border-radius: 50%;
            opacity: 0.5;
            transform: translateZ(0);

            @for $i from 1 through 3 {
                &:nth-child(#{$i}) {
                    animation: opacity-change 0.9s ease-in-out infinite;
                    animation-delay: #{($i - 1) * 0.3}s;
                }
            }
        }

        &--light .dot {
            background: #ffffff;
        }

    }

    @keyframes opacity-change {
        0%,
        100% {
            opacity: 0.5;
        }
        50% {
            opacity: 1;
        }
    }
</style>
